import { useState, useEffect, useCallback, useRef } from "react";
import { API, useAuth } from "../App";
import QMSLayout from "../components/QMSLayout";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  FileText, Plus, Loader2, Search, ChevronRight, ArrowLeft, Save,
  Sparkles, PenTool, Trash2, Edit3, Copy, CheckCircle2, Clock,
  AlertTriangle, ClipboardList, Send, X, GripVertical, ChevronDown,
  Download, Eye, Filter, LayoutTemplate, Database, Layers
} from "lucide-react";

const ax = axios.create({ withCredentials: true });

/* ─── Constants ────────────────────────────────────────────── */
const FIELD_TYPES = [
  { type: "text", label: "Text", icon: "Aa" },
  { type: "textarea", label: "Text Area", icon: "Tx" },
  { type: "number", label: "Number", icon: "#" },
  { type: "email", label: "Email", icon: "@" },
  { type: "date", label: "Date", icon: "D" },
  { type: "datetime", label: "Date & Time", icon: "DT" },
  { type: "select", label: "Dropdown", icon: "v" },
  { type: "multi_select", label: "Multi-Select", icon: "[]" },
  { type: "checkbox", label: "Checkbox", icon: "C" },
  { type: "radio", label: "Radio Group", icon: "O" },
  { type: "signature", label: "Signature", icon: "S" },
  { type: "section_header", label: "Section Header", icon: "H" },
];

const CATEGORY_MAP = {
  quality_audit: { label: "Quality Audit", color: "text-emerald-400", bg: "bg-emerald-400/10" },
  ncr: { label: "NCR", color: "text-red-400", bg: "bg-red-400/10" },
  capa: { label: "CAPA", color: "text-amber-400", bg: "bg-amber-400/10" },
  supplier: { label: "Supplier", color: "text-cyan-400", bg: "bg-cyan-400/10" },
  custom: { label: "Custom", color: "text-purple-400", bg: "bg-purple-400/10" },
};

const STATUS_MAP = {
  draft: { label: "Draft", color: "text-slate-400", bg: "bg-slate-400/10", icon: Clock },
  submitted: { label: "Submitted", color: "text-[#36A9E1]", bg: "bg-[#36A9E1]/10", icon: Send },
  approved: { label: "Approved", color: "text-emerald-400", bg: "bg-emerald-400/10", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "text-red-400", bg: "bg-red-400/10", icon: AlertTriangle },
};

/* ─── Unique ID generator ──────────────────────────────────── */
const uid = () => `f_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

/* ═══════════════════════════════════════════════════════════════ */
/*  MAIN COMPONENT                                                 */
/* ═══════════════════════════════════════════════════════════════ */
const DynamicFormsPage = () => {
  const { t } = useTranslation('modules');
  const { user } = useAuth();

  /* ─── State ── */
  const [view, setView] = useState("gallery"); // gallery | builder | filler | submissions | submission-detail
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  // Builder state
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Filler state
  const [fillingTemplate, setFillingTemplate] = useState(null);
  const [fillingValues, setFillingValues] = useState({});
  const [fillingSubmissionId, setFillingSubmissionId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiPopulating, setAiPopulating] = useState(false);

  // Submission detail
  const [viewingSubmission, setViewingSubmission] = useState(null);

  /* ─── Load Data ── */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, subRes, statsRes] = await Promise.all([
        ax.get(`${API}/forms/templates`),
        ax.get(`${API}/forms/submissions`),
        ax.get(`${API}/forms/stats`),
      ]);
      setTemplates(tplRes.data.templates || []);
      setSubmissions(subRes.data.submissions || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error("Failed to load forms data:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ─── Template Actions ── */
  const openBuilder = (template = null) => {
    if (template) {
      setEditingTemplate({ ...template, fields: [...(template.fields || []).map(f => ({ ...f }))] });
    } else {
      setEditingTemplate({
        name: "", description: "", category: "custom", sector: "all",
        fields: [
          { field_id: uid(), type: "text", label: "Form Title", required: true },
          { field_id: uid(), type: "date", label: "Date" },
        ],
      });
    }
    setView("builder");
  };

  const startFilling = (template, existingSubmission = null) => {
    setFillingTemplate(template);
    if (existingSubmission) {
      setFillingValues(existingSubmission.values || {});
      setFillingSubmissionId(existingSubmission.submission_id);
    } else {
      setFillingValues({});
      setFillingSubmissionId(null);
    }
    setView("filler");
  };

  const openSubmissions = () => {
    setView("submissions");
  };

  const viewSubmissionDetail = async (submission) => {
    try {
      const tpl = templates.find(t => t.template_id === submission.template_id);
      if (!tpl) {
        const tplRes = await ax.get(`${API}/forms/templates/${submission.template_id}`);
        setFillingTemplate(tplRes.data);
      } else {
        setFillingTemplate(tpl);
      }
      setViewingSubmission(submission);
      setView("submission-detail");
    } catch {}
  };

  const goBack = () => {
    setView("gallery");
    setEditingTemplate(null);
    setFillingTemplate(null);
    setFillingValues({});
    setFillingSubmissionId(null);
    setViewingSubmission(null);
    loadData();
  };

  /* ─── Filtered templates ── */
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase())
      || (t.description || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCat = !filterCategory || t.category === filterCategory;
    return matchesSearch && matchesCat;
  });

  /* ═══ LOADING ═══ */
  if (loading) {
    return (
      <QMSLayout title={t('modules:dynamicForms.title')}>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-[#36A9E1] animate-spin" />
        </div>
      </QMSLayout>
    );
  }

  /* ═══ TEMPLATE BUILDER VIEW ═══ */
  if (view === "builder" && editingTemplate) {
    return (
      <QMSLayout title={t('modules:dynamicForms.title')}>
        <TemplateBuilder
          template={editingTemplate}
          setTemplate={setEditingTemplate}
          onSave={async (tpl) => {
            try {
              if (tpl.template_id) {
                await ax.put(`${API}/forms/templates/${tpl.template_id}`, tpl);
              } else {
                await ax.post(`${API}/forms/templates`, tpl);
              }
              goBack();
            } catch (err) {
              alert("Failed to save template: " + (err.response?.data?.detail || err.message));
            }
          }}
          onCancel={goBack}
        />
      </QMSLayout>
    );
  }

  /* ═══ FORM FILLER VIEW ═══ */
  if (view === "filler" && fillingTemplate) {
    return (
      <QMSLayout title={fillingTemplate.name}>
        <FormFiller
          template={fillingTemplate}
          values={fillingValues}
          setValues={setFillingValues}
          submissionId={fillingSubmissionId}
          setSubmissionId={setFillingSubmissionId}
          saving={saving}
          setSaving={setSaving}
          aiPopulating={aiPopulating}
          setAiPopulating={setAiPopulating}
          user={user}
          onBack={goBack}
        />
      </QMSLayout>
    );
  }

  /* ═══ SUBMISSION DETAIL VIEW ═══ */
  if (view === "submission-detail" && viewingSubmission && fillingTemplate) {
    return (
      <QMSLayout title={t('modules:dynamicForms.title')}>
        <SubmissionDetail
          submission={viewingSubmission}
          template={fillingTemplate}
          onBack={() => { setView("submissions"); setViewingSubmission(null); }}
          onEdit={() => startFilling(fillingTemplate, viewingSubmission)}
        />
      </QMSLayout>
    );
  }

  /* ═══ SUBMISSIONS LIST VIEW ═══ */
  if (view === "submissions") {
    return (
      <QMSLayout title={t('modules:dynamicForms.title')}>
        <SubmissionsList
          submissions={submissions}
          templates={templates}
          onBack={goBack}
          onView={viewSubmissionDetail}
          onEdit={(sub) => {
            const tpl = templates.find(t => t.template_id === sub.template_id);
            if (tpl) startFilling(tpl, sub);
          }}
          onDelete={async (subId) => {
            try {
              await ax.delete(`${API}/forms/submissions/${subId}`);
              loadData();
            } catch {}
          }}
        />
      </QMSLayout>
    );
  }

  /* ═══ GALLERY VIEW (DEFAULT) ═══ */
  return (
    <QMSLayout title={t('modules:dynamicForms.title')}>
      <div className="space-y-6" data-testid="forms-gallery">
        {/* Stats bar */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label={t('forms.stats.templates')} value={stats.total_templates} icon={LayoutTemplate} color="text-[#36A9E1]" />
            <StatCard label={t('forms.stats.submissions')} value={stats.total_submissions} icon={ClipboardList} color="text-emerald-400" />
            <StatCard label={t('forms.stats.drafts')} value={stats.draft_count} icon={Clock} color="text-amber-400" />
            <StatCard label={t('forms.stats.approved')} value={stats.approved_count} icon={CheckCircle2} color="text-emerald-400" />
          </div>
        )}

        {/* Actions bar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B95A5]" />
              <input
                type="text"
                placeholder={t('forms.searchTemplates')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5] focus:outline-none focus:border-[#36A9E1]/50"
                data-testid="search-templates"
              />
            </div>
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="px-3 py-2 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white focus:outline-none"
              data-testid="filter-category"
            >
              <option value="">All Categories</option>
              {Object.entries(CATEGORY_MAP).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={openSubmissions}
              className="flex items-center gap-2 px-4 py-2 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] hover:border-[#36A9E1]/30 rounded text-sm text-[#CBD5E1] hover:text-white transition-colors"
              data-testid="view-submissions-btn"
            >
              <ClipboardList className="w-4 h-4" />
              Submissions ({submissions.length})
            </button>
            <button
              onClick={() => openBuilder()}
              className="flex items-center gap-2 px-4 py-2 bg-[#36A9E1] hover:bg-[#36A9E1]/80 rounded text-sm text-white font-medium transition-colors"
              data-testid="create-template-btn"
            >
              <Plus className="w-4 h-4" />
              New Template
            </button>
          </div>
        </div>

        {/* Template Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(tpl => {
            const cat = CATEGORY_MAP[tpl.category] || CATEGORY_MAP.custom;
            return (
              <div
                key={tpl.template_id}
                className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5 hover:border-[#36A9E1]/30 transition-all group"
                data-testid={`template-card-${tpl.template_id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${cat.bg} ${cat.color}`}>{cat.label}</span>
                    {tpl.is_prebuilt && (
                      <span className="text-xs px-2 py-0.5 rounded bg-[#36A9E1]/10 text-[#36A9E1]">Built-in</span>
                    )}
                  </div>
                  <span className="text-xs text-[#8B95A5]">v{tpl.version || "1.0"}</span>
                </div>

                <h3 className="text-white font-medium mb-1 text-sm">{tpl.name}</h3>
                <p className="text-[#8B95A5] text-xs mb-4 line-clamp-2">{tpl.description}</p>

                <div className="flex items-center justify-between text-xs text-[#8B95A5] mb-4">
                  <span>{(tpl.fields || []).filter(f => f.type !== "section_header").length} fields</span>
                  <span>{tpl.sector === "all" ? t('forms.allSectors') : tpl.sector}</span>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => startFilling(tpl)}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[#36A9E1]/10 hover:bg-[#36A9E1]/20 text-[#36A9E1] rounded text-xs font-medium transition-colors"
                    data-testid={`fill-form-${tpl.template_id}`}
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Fill Form
                  </button>
                  <button
                    onClick={() => openBuilder(tpl)}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 bg-[#181B23] hover:bg-[#2D313D] text-[#CBD5E1] rounded text-xs transition-colors"
                    data-testid={`edit-template-${tpl.template_id}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  {!tpl.is_prebuilt && (
                    <button
                      onClick={async () => {
                        if (window.confirm(t('forms.deleteTemplateConfirm'))) {
                          try {
                            await ax.delete(`${API}/forms/templates/${tpl.template_id}`);
                            loadData();
                          } catch {}
                        }
                      }}
                      className="flex items-center justify-center px-2 py-2 hover:bg-red-500/10 text-[#8B95A5] hover:text-red-400 rounded text-xs transition-colors"
                      data-testid={`delete-template-${tpl.template_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-16 text-[#8B95A5]">
            <LayoutTemplate className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No templates found</p>
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/*  STAT CARD                                                       */
/* ═══════════════════════════════════════════════════════════════ */
const StatCard = ({ label, value, icon: Icon, color }) => (
  <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 flex items-center gap-3" data-testid={`stat-${label.toLowerCase()}`}>
    <Icon className={`w-5 h-5 ${color}`} />
    <div>
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-xs text-[#8B95A5]">{label}</div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════════ */
/*  TEMPLATE BUILDER                                                */
/* ═══════════════════════════════════════════════════════════════ */
const TemplateBuilder = ({ template, setTemplate, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const updateField = (idx, key, value) => {
    const updated = [...template.fields];
    updated[idx] = { ...updated[idx], [key]: value };
    setTemplate({ ...template, fields: updated });
  };

  const addField = (type) => {
    const newField = {
      field_id: uid(),
      type,
      label: type === "section_header" ? "New Section" : "New Field",
      required: false,
      ...(["select", "multi_select", "radio"].includes(type) ? { options: ["Option 1", "Option 2"] } : {}),
    };
    setTemplate({ ...template, fields: [...template.fields, newField] });
  };

  const removeField = (idx) => {
    const updated = template.fields.filter((_, i) => i !== idx);
    setTemplate({ ...template, fields: updated });
  };

  const duplicateField = (idx) => {
    const dup = { ...template.fields[idx], field_id: uid() };
    const updated = [...template.fields];
    updated.splice(idx + 1, 0, dup);
    setTemplate({ ...template, fields: updated });
  };

  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };
  const handleDragEnd = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const updated = [...template.fields];
    const dragged = updated.splice(dragItem.current, 1)[0];
    updated.splice(dragOverItem.current, 0, dragged);
    dragItem.current = null;
    dragOverItem.current = null;
    setTemplate({ ...template, fields: updated });
  };

  const handleSave = async () => {
    if (!template.name.trim()) { alert("Template name is required"); return; }
    setSaving(true);
    await onSave(template);
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="template-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={onCancel} className="flex items-center gap-2 text-sm text-[#8B95A5] hover:text-white transition-colors" data-testid="builder-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2 bg-[#36A9E1] hover:bg-[#36A9E1]/80 disabled:opacity-50 rounded text-sm text-white font-medium transition-colors"
          data-testid="save-template-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Template
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Template meta */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-5 space-y-4">
            <input
              type="text"
              placeholder={t('forms.templateName')}
              value={template.name}
              onChange={e => setTemplate({ ...template, name: e.target.value })}
              className="w-full px-4 py-2.5 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-white text-lg font-medium placeholder-[#8B95A5] focus:outline-none focus:border-[#36A9E1]/50"
              data-testid="template-name-input"
            />
            <textarea
              placeholder="Description (optional)"
              value={template.description}
              onChange={e => setTemplate({ ...template, description: e.target.value })}
              className="w-full px-4 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-[#CBD5E1] placeholder-[#8B95A5] focus:outline-none resize-none h-16"
              data-testid="template-desc-input"
            />
            <div className="flex gap-3">
              <select
                value={template.category}
                onChange={e => setTemplate({ ...template, category: e.target.value })}
                className="px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white focus:outline-none flex-1"
                data-testid="template-category-select"
              >
                {Object.entries(CATEGORY_MAP).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </select>
              <select
                value={template.sector}
                onChange={e => setTemplate({ ...template, sector: e.target.value })}
                className="px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white focus:outline-none flex-1"
              >
                <option value="all">All Sectors</option>
                <option value="medical_devices">Medical Devices</option>
                <option value="pharmaceuticals">Pharmaceuticals</option>
                <option value="biotechnology">Biotechnology</option>
                <option value="aerospace">Aerospace</option>
                <option value="automotive">Automotive</option>
                <option value="industrial">Industrial</option>
              </select>
            </div>
          </div>

          {/* Field list */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-[#CBD5E1] mb-2">Form Fields ({template.fields.length})</h3>
            {template.fields.map((field, idx) => (
              <div
                key={field.field_id}
                draggable
                onDragStart={() => handleDragStart(idx)}
                onDragEnter={() => handleDragEnter(idx)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                className={`bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 group hover:border-[#36A9E1]/20 transition-all ${
                  field.type === "section_header" ? "border-l-2 border-l-[#E8A838]" : ""
                }`}
                data-testid={`builder-field-${field.field_id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="cursor-grab text-[#8B95A5] hover:text-white mt-1">
                    <GripVertical className="w-4 h-4" />
                  </div>

                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-1.5 py-0.5 bg-[#181B23] rounded text-[#8B95A5] font-mono">
                        {FIELD_TYPES.find(ft => ft.type === field.type)?.icon || "?"}
                      </span>
                      <input
                        type="text"
                        value={field.label}
                        onChange={e => updateField(idx, "label", e.target.value)}
                        className="flex-1 px-2 py-1 bg-transparent border-b border-transparent hover:border-[rgba(255,255,255,0.1)] focus:border-[#36A9E1]/50 text-sm text-white focus:outline-none"
                        data-testid={`field-label-${idx}`}
                      />
                    </div>

                    {field.type !== "section_header" && (
                      <div className="flex items-center gap-3 text-xs">
                        <select
                          value={field.type}
                          onChange={e => updateField(idx, "type", e.target.value)}
                          className="px-2 py-1 bg-[#181B23] border border-[rgba(255,255,255,0.06)] rounded text-[#CBD5E1] focus:outline-none"
                        >
                          {FIELD_TYPES.map(ft => (
                            <option key={ft.type} value={ft.type}>{ft.label}</option>
                          ))}
                        </select>
                        <label className="flex items-center gap-1 text-[#8B95A5] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={field.required || false}
                            onChange={e => updateField(idx, "required", e.target.checked)}
                            className="rounded"
                          />
                          Required
                        </label>
                        <input
                          type="text"
                          placeholder={t('forms.placeholder')}
                          value={field.placeholder || ""}
                          onChange={e => updateField(idx, "placeholder", e.target.value)}
                          className="px-2 py-1 bg-[#181B23] border border-[rgba(255,255,255,0.06)] rounded text-[#8B95A5] focus:outline-none flex-1 max-w-[200px]"
                        />
                      </div>
                    )}

                    {/* Options for select/radio/multi_select */}
                    {["select", "multi_select", "radio"].includes(field.type) && (
                      <div className="space-y-1 mt-1">
                        {(field.options || []).map((opt, oi) => (
                          <div key={oi} className="flex items-center gap-1">
                            <input
                              type="text"
                              value={opt}
                              onChange={e => {
                                const opts = [...(field.options || [])];
                                opts[oi] = e.target.value;
                                updateField(idx, "options", opts);
                              }}
                              className="px-2 py-0.5 bg-[#181B23] border border-[rgba(255,255,255,0.06)] rounded text-xs text-[#CBD5E1] focus:outline-none flex-1"
                            />
                            <button
                              onClick={() => {
                                const opts = (field.options || []).filter((_, i) => i !== oi);
                                updateField(idx, "options", opts);
                              }}
                              className="text-[#8B95A5] hover:text-red-400 p-0.5"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => updateField(idx, "options", [...(field.options || []), "New Option"])}
                          className="text-xs text-[#36A9E1] hover:text-white"
                        >
                          + Add Option
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => duplicateField(idx)} className="p-1 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeField(idx)} className="p-1 hover:bg-red-500/10 rounded text-[#8B95A5] hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Field palette */}
        <div>
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 sticky top-4">
            <h4 className="text-xs font-medium text-[#8B95A5] uppercase tracking-wider mb-3">Add Field</h4>
            <div className="grid grid-cols-2 gap-2">
              {FIELD_TYPES.map(ft => (
                <button
                  key={ft.type}
                  onClick={() => addField(ft.type)}
                  className="flex items-center gap-2 px-3 py-2 bg-[#181B23] hover:bg-[#2D313D] border border-[rgba(255,255,255,0.06)] rounded text-xs text-[#CBD5E1] hover:text-white transition-colors"
                  data-testid={`add-field-${ft.type}`}
                >
                  <span className="font-mono text-[#36A9E1] w-5 text-center">{ft.icon}</span>
                  {ft.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/*  FORM FILLER                                                     */
/* ═══════════════════════════════════════════════════════════════ */
const FormFiller = ({
  template, values, setValues, submissionId, setSubmissionId,
  saving, setSaving, aiPopulating, setAiPopulating, user, onBack,
}) => {
  const [scriberSessions, setScriberSessions] = useState([]);
  const [showScriberPanel, setShowScriberPanel] = useState(false);
  const [importingSrc, setImportingSrc] = useState(false);

  useEffect(() => {
    ax.get(`${API}/scriber/sessions`).then(r => setScriberSessions(r.data.sessions || [])).catch(() => {});
  }, []);

  const handleFieldChange = (fieldId, value) => {
    setValues(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleSave = async (status = "draft") => {
    setSaving(true);
    try {
      const payload = {
        template_id: template.template_id,
        values,
        status,
        ...(submissionId ? { submission_id: submissionId } : {}),
      };
      const res = await ax.post(`${API}/forms/submissions`, payload);
      setSubmissionId(res.data.submission_id);
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const handleAiPopulate = async (source = "all") => {
    setAiPopulating(true);
    try {
      const res = await ax.post(`${API}/forms/ai-populate`, {
        template_id: template.template_id,
        context_source: source,
        existing_values: values,
      });
      if (res.data.suggested_values) {
        setValues(prev => {
          const merged = { ...prev };
          for (const [k, v] of Object.entries(res.data.suggested_values)) {
            if (!merged[k] && v) merged[k] = v;
          }
          return merged;
        });
      }
    } catch (err) {
      alert("AI populate failed: " + (err.response?.data?.detail || err.message));
    }
    setAiPopulating(false);
  };

  const handleScriberImport = async (sessionId) => {
    setImportingSrc(true);
    try {
      const res = await ax.post(`${API}/forms/import-scriber`, {
        template_id: template.template_id,
        scriber_session_id: sessionId,
      });
      if (res.data.mapped_values) {
        setValues(prev => {
          const merged = { ...prev };
          for (const [k, v] of Object.entries(res.data.mapped_values)) {
            if (!merged[k] && v) merged[k] = v;
          }
          return merged;
        });
      }
      setShowScriberPanel(false);
    } catch (err) {
      alert("Import failed: " + (err.response?.data?.detail || err.message));
    }
    setImportingSrc(false);
  };

  const cat = CATEGORY_MAP[template.category] || CATEGORY_MAP.custom;

  return (
    <div className="space-y-4" data-testid="form-filler">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#8B95A5] hover:text-white" data-testid="filler-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span className={`text-xs px-2 py-0.5 rounded ${cat.bg} ${cat.color}`}>{cat.label}</span>
          {submissionId && <span className="text-xs text-[#8B95A5] font-mono">{submissionId}</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => handleAiPopulate("all")}
            disabled={aiPopulating}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded text-xs font-medium transition-colors disabled:opacity-50"
            data-testid="ai-populate-btn"
          >
            {aiPopulating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            AI Populate
          </button>
          <button
            onClick={() => setShowScriberPanel(!showScriberPanel)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#E8A838]/10 hover:bg-[#E8A838]/20 text-[#E8A838] rounded text-xs font-medium transition-colors"
            data-testid="scriber-import-btn"
          >
            <PenTool className="w-3.5 h-3.5" />
            SCRIBER Import
          </button>
          <button
            onClick={() => handleSave("draft")}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] hover:border-[#36A9E1]/30 text-[#CBD5E1] rounded text-xs font-medium transition-colors disabled:opacity-50"
            data-testid="save-draft-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save Draft
          </button>
          <button
            onClick={() => handleSave("submitted")}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#36A9E1] hover:bg-[#36A9E1]/80 text-white rounded text-xs font-medium transition-colors disabled:opacity-50"
            data-testid="submit-form-btn"
          >
            <Send className="w-3.5 h-3.5" />
            Submit
          </button>
        </div>
      </div>

      {/* SCRIBER Panel */}
      {showScriberPanel && (
        <div className="bg-[#1F222D] border border-[#E8A838]/20 rounded-lg p-4" data-testid="scriber-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-[#E8A838]">Import from SCRIBER</h4>
            <button onClick={() => setShowScriberPanel(false)} className="text-[#8B95A5] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          {scriberSessions.length === 0 ? (
            <p className="text-xs text-[#8B95A5]">No SCRIBER sessions with transcriptions found. Create a SCRIBER session first.</p>
          ) : (
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {scriberSessions.filter(s => s.has_transcription).map(s => (
                <button
                  key={s.session_id}
                  onClick={() => handleScriberImport(s.session_id)}
                  disabled={importingSrc}
                  className="w-full flex items-center justify-between px-3 py-2 bg-[#181B23] hover:bg-[#2D313D] rounded text-xs text-[#CBD5E1] transition-colors disabled:opacity-50"
                  data-testid={`import-session-${s.session_id}`}
                >
                  <div className="flex items-center gap-2">
                    <PenTool className="w-3 h-3 text-[#E8A838]" />
                    <span>{s.label || s.session_id}</span>
                  </div>
                  <span className="text-[#8B95A5]">{s.page_count} pages</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form fields */}
      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-1">{template.name}</h2>
        <p className="text-xs text-[#8B95A5] mb-6">{template.description}</p>

        <div className="space-y-5">
          {(template.fields || []).map(field => (
            <FieldRenderer
              key={field.field_id}
              field={field}
              value={values[field.field_id] || ""}
              onChange={val => handleFieldChange(field.field_id, val)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/*  FIELD RENDERER                                                  */
/* ═══════════════════════════════════════════════════════════════ */
const FieldRenderer = ({ field, value, onChange, readOnly = false }) => {
  if (field.type === "section_header") {
    return (
      <div className="pt-4 pb-1 border-b border-[#E8A838]/20">
        <h3 className="text-sm font-semibold text-[#E8A838]">{field.label}</h3>
      </div>
    );
  }

  const labelEl = (
    <label className="block text-sm text-[#CBD5E1] mb-1">
      {field.label}
      {field.required && <span className="text-red-400 ml-1">*</span>}
    </label>
  );

  const baseInput = "w-full px-3 py-2 bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white placeholder-[#8B95A5] focus:outline-none focus:border-[#36A9E1]/50 disabled:opacity-60";

  switch (field.type) {
    case "text":
    case "email":
    case "number":
      return (
        <div data-testid={`field-${field.field_id}`}>
          {labelEl}
          <input
            type={field.type}
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={field.placeholder || ""}
            disabled={readOnly}
            className={baseInput}
          />
        </div>
      );

    case "textarea":
      return (
        <div data-testid={`field-${field.field_id}`}>
          {labelEl}
          <textarea
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={field.placeholder || ""}
            disabled={readOnly}
            rows={3}
            className={`${baseInput} resize-y`}
          />
        </div>
      );

    case "date":
    case "datetime":
      return (
        <div data-testid={`field-${field.field_id}`}>
          {labelEl}
          <input
            type={field.type === "datetime" ? "datetime-local" : "date"}
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={readOnly}
            className={baseInput}
          />
        </div>
      );

    case "select":
      return (
        <div data-testid={`field-${field.field_id}`}>
          {labelEl}
          <select
            value={value}
            onChange={e => onChange(e.target.value)}
            disabled={readOnly}
            className={baseInput}
          >
            <option value="">Select...</option>
            {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      );

    case "multi_select":
      return (
        <div data-testid={`field-${field.field_id}`}>
          {labelEl}
          <div className="flex flex-wrap gap-2">
            {(field.options || []).map(opt => {
              const selected = Array.isArray(value) ? value.includes(opt) : false;
              return (
                <button
                  key={opt}
                  onClick={() => {
                    if (readOnly) return;
                    const arr = Array.isArray(value) ? [...value] : [];
                    if (arr.includes(opt)) onChange(arr.filter(v => v !== opt));
                    else onChange([...arr, opt]);
                  }}
                  className={`px-3 py-1 rounded text-xs border transition-colors ${
                    selected
                      ? "bg-[#36A9E1]/20 border-[#36A9E1]/40 text-[#36A9E1]"
                      : "bg-[#181B23] border-[rgba(255,255,255,0.08)] text-[#8B95A5] hover:text-white"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      );

    case "checkbox":
      return (
        <div data-testid={`field-${field.field_id}`} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value === true || value === "true"}
            onChange={e => onChange(e.target.checked)}
            disabled={readOnly}
            className="rounded"
          />
          <span className="text-sm text-[#CBD5E1]">{field.label}</span>
          {field.required && <span className="text-red-400 text-xs">*</span>}
        </div>
      );

    case "radio":
      return (
        <div data-testid={`field-${field.field_id}`}>
          {labelEl}
          <div className="flex flex-wrap gap-3">
            {(field.options || []).map(opt => (
              <label key={opt} className="flex items-center gap-1.5 text-sm text-[#CBD5E1] cursor-pointer">
                <input
                  type="radio"
                  name={field.field_id}
                  value={opt}
                  checked={value === opt}
                  onChange={() => onChange(opt)}
                  disabled={readOnly}
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      );

    case "signature":
      return (
        <div data-testid={`field-${field.field_id}`}>
          {labelEl}
          <div className="bg-[#181B23] border border-[rgba(255,255,255,0.08)] rounded p-4 text-center">
            {value ? (
              <div>
                <p className="text-sm text-emerald-400 italic">{value}</p>
                {!readOnly && <button onClick={() => onChange("")} className="text-xs text-red-400 mt-1">Clear</button>}
              </div>
            ) : readOnly ? (
              <p className="text-xs text-[#8B95A5]">Not signed</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-[#8B95A5]">Type your name to sign</p>
                <input
                  type="text"
                  placeholder="Full Name"
                  onKeyDown={e => { if (e.key === "Enter") onChange(e.target.value); }}
                  onBlur={e => { if (e.target.value) onChange(e.target.value); }}
                  className="px-3 py-1.5 bg-[#111318] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white text-center focus:outline-none w-60"
                />
              </div>
            )}
          </div>
        </div>
      );

    default:
      return (
        <div data-testid={`field-${field.field_id}`}>
          {labelEl}
          <input type="text" value={value} onChange={e => onChange(e.target.value)} disabled={readOnly} className={baseInput} />
        </div>
      );
  }
};

/* ═══════════════════════════════════════════════════════════════ */
/*  SUBMISSIONS LIST                                                */
/* ═══════════════════════════════════════════════════════════════ */
const SubmissionsList = ({ submissions, templates, onBack, onView, onEdit, onDelete }) => {
  const [filterStatus, setFilterStatus] = useState("");

  const filtered = submissions.filter(s => !filterStatus || s.status === filterStatus);

  return (
    <div className="space-y-4" data-testid="submissions-list">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#8B95A5] hover:text-white" data-testid="submissions-back">
          <ArrowLeft className="w-4 h-4" /> Back to Templates
        </button>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-1.5 bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded text-sm text-white focus:outline-none"
          data-testid="filter-status"
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_MAP).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-[#8B95A5]">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No submissions yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(sub => {
            const st = STATUS_MAP[sub.status] || STATUS_MAP.draft;
            const StIcon = st.icon;
            return (
              <div
                key={sub.submission_id}
                className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-4 flex items-center justify-between hover:border-[#36A9E1]/20 transition-all"
                data-testid={`submission-row-${sub.submission_id}`}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <StIcon className={`w-4 h-4 ${st.color} flex-shrink-0`} />
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{sub.template_name || sub.template_id}</p>
                    <div className="flex items-center gap-2 text-xs text-[#8B95A5]">
                      <span className={`${st.color}`}>{st.label}</span>
                      <span>by {sub.submitted_by_name || sub.submitted_by}</span>
                      <span>{new Date(sub.updated_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-3">
                  <button
                    onClick={() => onView(sub)}
                    className="p-1.5 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-white"
                    data-testid={`view-sub-${sub.submission_id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {sub.status === "draft" && (
                    <button
                      onClick={() => onEdit(sub)}
                      className="p-1.5 hover:bg-[#2D313D] rounded text-[#8B95A5] hover:text-[#36A9E1]"
                      data-testid={`edit-sub-${sub.submission_id}`}
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                  )}
                  {sub.status === "draft" && (
                    <button
                      onClick={() => { if (window.confirm("Delete this draft?")) onDelete(sub.submission_id); }}
                      className="p-1.5 hover:bg-red-500/10 rounded text-[#8B95A5] hover:text-red-400"
                      data-testid={`delete-sub-${sub.submission_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════ */
/*  SUBMISSION DETAIL (READ-ONLY)                                   */
/* ═══════════════════════════════════════════════════════════════ */
const SubmissionDetail = ({ submission, template, onBack, onEdit }) => {
  const st = STATUS_MAP[submission.status] || STATUS_MAP.draft;
  const StIcon = st.icon;
  const cat = CATEGORY_MAP[template?.category] || CATEGORY_MAP.custom;

  return (
    <div className="space-y-4" data-testid="submission-detail">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2 text-sm text-[#8B95A5] hover:text-white" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" /> Back to Submissions
        </button>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${st.bg} ${st.color}`}>
            <StIcon className="w-3 h-3" /> {st.label}
          </span>
          {submission.status === "draft" && (
            <button
              onClick={onEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#36A9E1]/10 hover:bg-[#36A9E1]/20 text-[#36A9E1] rounded text-xs"
              data-testid="edit-submission-btn"
            >
              <Edit3 className="w-3 h-3" /> Edit
            </button>
          )}
        </div>
      </div>

      <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.08)] rounded-lg p-6">
        <div className="flex items-center gap-2 mb-1">
          <span className={`text-xs px-2 py-0.5 rounded ${cat.bg} ${cat.color}`}>{cat.label}</span>
          <span className="text-xs text-[#8B95A5] font-mono">{submission.submission_id}</span>
        </div>
        <h2 className="text-lg font-semibold text-white mb-1">{template?.name || submission.template_name}</h2>
        <p className="text-xs text-[#8B95A5] mb-6">
          Submitted by {submission.submitted_by_name} on {new Date(submission.updated_at).toLocaleDateString()}
        </p>

        <div className="space-y-5">
          {(template?.fields || []).map(field => (
            <FieldRenderer
              key={field.field_id}
              field={field}
              value={submission.values?.[field.field_id] || ""}
              onChange={() => {}}
              readOnly
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DynamicFormsPage;
